import React, { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { noop } from 'lodash';

import RegisterFeature from '@features/register';
import { useRegisterForm } from '@features/register/register-page-context';
import {
  useElearningWpRemoteDataProtectionDeclineMutation,
  useElearningWpRemotePersonInfoQuery,
  useElearningWpRemoteUpdatePersonMutation
} from '@services/elearning-wp-remote';
import { useConfigurationStore } from '@store/use-configuration-store';
import { useElearningWpRemoteStore } from '@store/use-ewr-store';
import { useUserStore } from '@store/use-user-store';

import { useElearningWpRemoteFlow } from './shared/ewr-flow-context';

import type { RegisterFormData } from '@types';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@constants/routes';

function ElearningWpRemoteRegisterPage() {
  const { goBack, goNext } = useElearningWpRemoteFlow();
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId, setEwrContactId } = useElearningWpRemoteStore();
  const navigate = useNavigate();

  const formProps = useRegisterForm();

  const personInfoQuery = useElearningWpRemotePersonInfoQuery(friendly_url);
  const person = personInfoQuery.data?.people?.[0] ?? null;

  useEffect(() => {
    if (person?.id != null) setEwrContactId(person.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.id]);

  const prefillDone = useRef(false);
  useEffect(() => {
    if (prefillDone.current || personInfoQuery.isFetching || !person) return;
    prefillDone.current = true;
    formProps.setFormField('fullname', person.name ?? '');
    formProps.setFormField('mothersName', person.motherName ?? '');
    formProps.setFormField(
      'dateOfBirth',
      person.birthDate ? dayjs(person.birthDate) : null
    );
    formProps.setFormField('documentType', person.documentType ?? '');
    formProps.setFormField('docNo', person.documentNumber ?? '');
    formProps.setFormField(
      'docExpiryDate',
      person.documentExpiryDate ? dayjs(person.documentExpiryDate) : null
    );
    formProps.setFormField('phoneNumber', person.phonenumber ?? '');
    formProps.setFormField('companyName', person.companyName ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personInfoQuery.isFetching]);

  const updateMutation = useElearningWpRemoteUpdatePersonMutation(
    friendly_url,
    ewrContactId
  );
  const declineMutation = useElearningWpRemoteDataProtectionDeclineMutation(
    friendly_url,
    ewrContactId
  );

  const onValidSubmit = (formData: RegisterFormData) => {
    const body = {
      name: formData.fullname || null,
      birthDate: formData.dateOfBirth
        ? format(formData.dateOfBirth.toDate(), 'yyyy-MM-dd')
        : null,
      motherName: formData.mothersName || null,
      documentType: formData.documentType || null,
      documentNumber: formData.docNo || null,
      documentExpiryDate: formData.docExpiryDate
        ? format(formData.docExpiryDate.toDate(), 'yyyy-MM-dd')
        : null,
      phonenumber: formData.phoneNumber || null,
      expiry: formData.expiry || null
    };
    return updateMutation.mutateAsync(body).then(goNext).catch(noop);
  };

  return (
    <RegisterFeature
      {...formProps}
      registeredPersons={[]}
      editRegistrantId={null}
      setEditRegistrantId={() => {}}
      addRegistrant={() => {}}
      removeRegistrant={() => {}}
      updateRegistrant={() => {}}
      onValidSubmit={onValidSubmit}
      onDataProtectionDecline={() =>
        declineMutation.mutateAsync().then(() => navigate(ROUTES.EWR_LOGOUT))
      }
      isSubmitting={updateMutation.isPending || declineMutation.isPending}
      onPreviousPage={goBack}
    />
  );
}

export default ElearningWpRemoteRegisterPage;
